/**
 * Initial skeleton for category loading - shown only on first load
 * Once categories are cached, content renders immediately
 */
import * as React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { Container } from 'reactstrap';

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: 8px;
`;

const SkeletonText = styled(SkeletonBase)<{ width?: string; height?: string }>`
  width: ${({ width }) => width || '100%'};
  height: ${({ height }) => height || '14px'};
`;

const CategorySection = styled.div`
  padding: 48px 0;
  border-bottom: 1px solid #f0f0f0;
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 32px;
  gap: 16px;
`;

const CategoryInfo = styled.div`
  flex: 1;
`;

const SkeletonButton = styled(SkeletonBase)`
  width: 100px;
  height: 44px;
  border-radius: 24px;
`;

const StyleIndicator = styled(SkeletonBase)`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 8px;
`;

// Cinematic Grid - first category
const CinematicGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 24px;
  @media (max-width: 600px) { grid-template-columns: 1fr; }
`;

const CinematicCard = styled(SkeletonBase)`
  aspect-ratio: 16 / 9;
  border-radius: 16px;
`;

// Tiles Grid - second category
const TilesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  @media (max-width: 1200px) { grid-template-columns: repeat(3, 1fr); }
  @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
`;

const TileCard = styled(SkeletonBase)`
  aspect-ratio: 1;
  border-radius: 20px;
`;

// Magazine Grid - third category
const MagazineGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const MagazineCard = styled.div`
  display: flex;
  background: white;
  border-radius: 16px;
  overflow: hidden;
  height: 180px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  @media (max-width: 600px) { height: 140px; }
`;

const MagazineImage = styled(SkeletonBase)`
  width: 40%;
  min-width: 140px;
  border-radius: 0;
`;

const MagazineContent = styled.div`
  flex: 1;
  padding: 20px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 12px;
`;

type SkeletonSectionProps = {
  style: 'cinematic' | 'tiles' | 'magazine';
  isLast?: boolean;
};

const SkeletonSection: React.FC<SkeletonSectionProps> = ({ style, isLast }) => (
  <CategorySection style={{ borderBottom: isLast ? 'none' : undefined }}>
    <Container>
      <CategoryHeader>
        <CategoryInfo>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <StyleIndicator />
            <SkeletonText width="200px" height="28px" />
          </div>
          <SkeletonText width="350px" height="16px" />
        </CategoryInfo>
        <SkeletonButton />
      </CategoryHeader>
      {style === 'cinematic' && (
        <CinematicGrid>
          {Array.from({ length: 4 }).map((_, i) => <CinematicCard key={i} />)}
        </CinematicGrid>
      )}
      {style === 'tiles' && (
        <TilesGrid>
          {Array.from({ length: 8 }).map((_, i) => <TileCard key={i} />)}
        </TilesGrid>
      )}
      {style === 'magazine' && (
        <MagazineGrid>
          {Array.from({ length: 6 }).map((_, i) => (
            <MagazineCard key={i}>
              <MagazineImage />
              <MagazineContent>
                <SkeletonText width="30%" height="3px" />
                <SkeletonText width="40%" height="11px" />
                <SkeletonText width="80%" height="18px" />
                <SkeletonText width="100%" height="14px" />
              </MagazineContent>
            </MagazineCard>
          ))}
        </MagazineGrid>
      )}
    </Container>
  </CategorySection>
);

export const InitialCategoriesSkeleton: React.FC = () => (
  <>
    <SkeletonSection style="cinematic" />
    <SkeletonSection style="tiles" />
    <SkeletonSection style="magazine" isLast />
  </>
);
